import { Box, Divider, Tab, Tabs, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { useState } from 'react';
import theme from '../../theme';
import CurrentOrders from './CurrentOrders';
import OrderHistory from './OrderHistory';
import CustomersTable from './CustomersTable';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '16px',
    color: 'text.secondary',
    '&.Mui-selected': {
      color: theme.palette.custom.textBlue,
      fontWeight: 500,
      backgroundColor: theme.palette.custom.sidebarActiveBg,
      borderRadius: '6px',
    },
  },
});

const TabPanel: React.FC<{
  children: React.ReactNode;
  value: number;
  index: number;
}> = ({ children, value, index }) => {
  return value === index ? <>{children}</> : null;
};

const OrderManagementTabs: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const today = new Date();
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(today.getDate() - 3);
    return twoDaysAgo;
  });
  const [toDate, setToDate] = useState<Date | null>(() => {
    // Set default to yesterday instead of today
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  });

  // Calculate yesterday for calendar restrictions
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const tabItems = [
    {
      label: 'Current Orders',
      component: <CurrentOrders />,
    },
    {
      label: 'Order History',
      component: (
        <OrderHistory
          fromDate={fromDate || new Date()}
          toDate={toDate || new Date()}
        />
      ),
    },
    {
      label: 'Customers',
      component: <CustomersTable />,
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const commonBoxStyles = {
    display: 'flex',
    borderRadius: '8px',
    border: '1px solid #DCE0E7',
    backgroundColor: '#FFF',
    height: '100%',
  };

  const commonContentStyles = {
    width: '100%',
  };

  return (
    <Box sx={commonBoxStyles}>
      <Box sx={commonContentStyles}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            padding: '10px 16px',
          }}
        >
          <StyledTabs value={selectedTab} onChange={handleTabChange}>
            {tabItems.map((tab, i) => (
              <Tab key={i} label={tab.label} />
            ))}
          </StyledTabs>

          {selectedTab === 1 && (
            <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DemoContainer components={['DatePicker']}>
                  <DatePicker
                    label="From Date"
                    value={fromDate}
                    views={['year', 'month', 'day']}
                    onChange={(newValue: Date | null) => setFromDate(newValue)}
                    format="dd-MM-yyyy"
                    maxDate={yesterday} // Changed from new Date() to yesterday
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 150,
                          '& input': {
                            fontSize: '14px',
                            color: theme.palette.lightGray.text,
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                          },
                        },
                      },
                    }}
                  />

                  <DatePicker
                    label="To Date"
                    value={toDate}
                    views={['year', 'month', 'day']}
                    onChange={(newValue: Date | null) => setToDate(newValue)}
                    format="dd-MM-yyyy"
                    minDate={fromDate || undefined}
                    maxDate={yesterday} // Changed from new Date() to yesterday
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: 150,
                          '& input': {
                            fontSize: '14px',
                            color: theme.palette.lightGray.text,
                          },
                          '& .MuiSvgIcon-root': {
                            fontSize: '20px',
                          },
                        },
                      },
                    }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Box>
          )}
        </Box>
        <Divider />
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            height: 'calc(100vh - 200px)',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            p: 2,
          }}
        >
          {tabItems.map((tab, index) => (
            <TabPanel key={index} value={selectedTab} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default OrderManagementTabs;
